import { useState, useCallback, useEffect } from 'react';
import { playbookApi } from '../api/playbookApi';
import type { PlaybookEntity, SimulationResult, ValidationResult } from '../types/playbook.types';

export function usePlaybookDetail(playbookId: string | null) {
  const [playbook, setPlaybook] = useState<PlaybookEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!playbookId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await playbookApi.getPlaybook(playbookId);
      setPlaybook(res as any);
    } catch (err) {
      console.error('[usePlaybookDetail] Failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load playbook');
    } finally {
      setLoading(false);
    }
  }, [playbookId]);

  useEffect(() => {
    load();
  }, [load]);

  const simulate = useCallback(async (allPaths = false): Promise<SimulationResult> => {
    if (!playbookId) throw new Error('No playbook selected');
    const res = await playbookApi.simulatePlaybook(playbookId, { allPaths });
    return res as any;
  }, [playbookId]);

  const validate = useCallback(async (): Promise<ValidationResult> => {
    if (!playbookId) throw new Error('No playbook selected');
    const res = await playbookApi.validatePlaybook(playbookId);
    return res as any;
  }, [playbookId]);

  const publish = useCallback(async (): Promise<PlaybookEntity> => {
    if (!playbookId) throw new Error('No playbook selected');
    const res = await playbookApi.publishPlaybook(playbookId);
    const data = res as any;
    setPlaybook(data);
    return data;
  }, [playbookId]);

  return { playbook, loading, error, refresh: load, simulate, validate, publish };
}
